import { Eye, EyeOff } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export type ColSpan = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export const smSpan = {
  1: 'sm:col-span-1',
  2: 'sm:col-span-2',
  3: 'sm:col-span-3',
  4: 'sm:col-span-4',
  5: 'sm:col-span-5',
  6: 'sm:col-span-6',
  7: 'sm:col-span-7',
  8: 'sm:col-span-8',
  9: 'sm:col-span-9',
  10: 'sm:col-span-10',
  11: 'sm:col-span-11',
  12: 'sm:col-span-12',
} as const;

export const mdSpan = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12',
} as const;

export const lgSpan = {
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  4: 'lg:col-span-4',
  5: 'lg:col-span-5',
  6: 'lg:col-span-6',
  7: 'lg:col-span-7',
  8: 'lg:col-span-8',
  9: 'lg:col-span-9',
  10: 'lg:col-span-10',
  11: 'lg:col-span-11',
  12: 'lg:col-span-12',
} as const;

/**
 * Props que todo componente de `form-fields` acaba redeclarando. Componentes
 * novos devem estender esse tipo em vez de repetir name/control/label/sm/md/lg/required/className.
 */
export type CommonFormFieldProps<T extends FieldValues = FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;

  sm?: ColSpan;
  md?: ColSpan;
  lg?: ColSpan;

  required?: boolean;
  className?: string;
};

type FormInputProps<T extends FieldValues> = CommonFormFieldProps<T> & {
  placeholder?: string;
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  autoComplete?: string;

  leftAddon?: ReactNode;
  rightAddon?: ReactNode;
} & React.ComponentProps<'input'>;

export function FormInput<T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  type = 'text',
  autoComplete,
  leftAddon,
  rightAddon,
  sm = 12,
  md = 4,
  lg = 4,
  className,
  ...props
}: FormInputProps<T>) {
  const [showPassword, setShowPassword] = useState(false);

  const isPassword = type === 'password';

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field
          className={cn(sm && smSpan[sm], md && mdSpan[md], lg && lgSpan[lg], className)}
          aria-required
          data-invalid={fieldState.invalid}
        >
          <FieldLabel htmlFor={name}>
            {label}
            {props?.required && <strong className="text-red-600">*</strong>}
          </FieldLabel>

          <InputGroup>
            <InputGroupInput
              {...field}
              {...props}
              value={field.value ?? ''}
              id={name}
              disabled={field.disabled ?? props.disabled}
              placeholder={placeholder}
              type={isPassword ? (showPassword ? 'text' : 'password') : type}
              autoComplete={autoComplete}
              aria-invalid={fieldState.invalid}
            />

            {(isPassword || rightAddon) && (
              <InputGroupAddon align="inline-end">
                {isPassword ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <InputGroupButton
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <Eye /> : <EyeOff />}
                      </InputGroupButton>
                    </TooltipTrigger>

                    <TooltipContent>
                      {showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    </TooltipContent>
                  </Tooltip>
                ) : rightAddon ? (
                  rightAddon
                ) : null}
              </InputGroupAddon>
            )}

            {leftAddon && (
              <InputGroupAddon align="inline-start">{leftAddon}</InputGroupAddon>
            )}
          </InputGroup>

          {fieldState.error && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}
